import { useContext, useEffect, useMemo, useSyncExternalStore } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container, DataField, Divider, P, H1 } from "./common-components";
import { FormHeader } from "./FormHeader";
import { FormContextMA } from "../form-context";
import { GetPatientAddress } from "../common/GetPatientAddress";
import { ReactSVG } from "react-svg";
import { ClinicalServiceTypes } from "../../patient/text/data";
import {
  capitalizeFirstLetter,
  formatHealth,
  formatPhoneDocumentation,
  formatToYYYY_MMM_DD,
} from "../../../helpers/functions";
import { frequencyOptions, getFullLabelByValue, reasonOptions } from "../../patient/clinical-services/service-steps/clinical-assessment/data";
import { FormFooter } from "./FormFooter";
import { ReferralAlertInformation } from "./ReferralAlertInformation";
import { BillForService } from "./BillForService";
import { AlertConfirmationTypes } from "../../../helpers/constant";
import { AllergicRhinitisDocTypes } from "../../patient/clinical-services/service-steps/documentation/data";
import { GetDropDownValue } from "./data";
import { getCurrentUserDetails } from "../../../redux/modules/user/actions";

const parseStorageMap = (raw) => {
  try {
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    return {};
  }
};

const getSessionStorageValue = (key) => () => sessionStorage.getItem(key) || "";

const createStorageSubscriber = (eventName) => (cb) => {
  window.addEventListener(eventName, cb);
  return () => window.removeEventListener(eventName, cb);
};

const useSessionStorageMap = (storageKey, changeEventName) => {
  const rawValue = useSyncExternalStore(
    createStorageSubscriber(changeEventName),
    getSessionStorageValue(storageKey),
    getSessionStorageValue(storageKey)
  );
  return useMemo(() => parseStorageMap(rawValue), [rawValue]);
};

const TRUE_STRING_VALUES = new Set(["true", "1", "yes"]);
const FALSE_STRING_VALUES = new Set(["false", "0", "no"]);

const getMedicationNotesKey = (id, medicationName) =>
  id != null && id !== ""
    ? `id:${id}`
    : `name:${String(medicationName || "")
        .trim()
        .toLowerCase()}`;

const parseBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (TRUE_STRING_VALUES.has(normalized)) return true;
    if (FALSE_STRING_VALUES.has(normalized)) return false;
  }
  return Boolean(value);
};

const getNoteOverrideKeys = (drug) =>
  [
    getMedicationNotesKey(drug?.id, drug?.medicationName),
    getMedicationNotesKey(drug?.medicationId, drug?.medicationName),
    getMedicationNotesKey(drug?.drugId, drug?.medicationName),
    getMedicationNotesKey(null, drug?.medicationName),
    getMedicationNotesKey(null, drug?.displayText),
  ].filter(Boolean);

const resolveNoteOverride = (notesMap, drug) => {
  const keys = getNoteOverrideKeys(drug);
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(notesMap, key)) return notesMap[key];
  }
  return null;
};

export const PrescriptionDetails = ({ drug }) => {
  const { formType, patient, clinicalServiceName, startDateTime, alertData } =
    useContext(FormContextMA);

  const { currentLoggedInUserDetail } = useSelector((state) => state.currentLoggedInUserDetail);
  const usersState = useSelector((state) => state.users);
  const dispatch = useDispatch();

  // Helper to compute pharmacist full name once
  const getFullName = (u) => (u?.firstName && u?.lastName ? `${u.firstName} ${u.lastName}` : null);
  const pharmacistName =
    getFullName(currentLoggedInUserDetail) ??
    getFullName(usersState?.currentLoggedInUserDetail) ??
    "-";

  // Helper to format signature URL
  const getSignatureUrl = (u) => {
    if (!u?.signature || typeof u.signature !== 'string' || u.signature.length === 0) return null;
    return u.signature.startsWith('data:') ? u.signature : `data:image/png;base64,${u.signature}`;
  };
  const pharmacistSignature =
    getSignatureUrl(currentLoggedInUserDetail) ??
    getSignatureUrl(usersState?.currentLoggedInUserDetail) ??
    null;

  // Helper to get license number
  const getLicenseNumber = (u) => {
    if (!u?.driverLicence || typeof u.driverLicence !== 'string' || u.driverLicence.trim().length === 0) return "";
    return u.driverLicence.trim();
  };
  const pharmacistLicenseNumber =
    getLicenseNumber(currentLoggedInUserDetail) ||
    getLicenseNumber(usersState?.currentLoggedInUserDetail) ||
    "";

  // Fetch user details if not available
  useEffect(() => {
    if (!currentLoggedInUserDetail) {
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
      const userId = userInfo.userId;
      
      if (userId) {
        dispatch(getCurrentUserDetails(userId, false));
      }
    }
  }, [currentLoggedInUserDetail, dispatch]);

  const isValidDate = (d) => {
    const part = (d || "").split("T")[0];
    return !!part && part !== "0001-01-01";
  };

  // Allow preview updates driven by the confirmation modal.
  const pharmacyLocationsMap = useSessionStorageMap(
    "treatmentPharmacyLocations",
    "treatmentPharmacyLocationsChanged"
  );
  const overridePharmacyLocation =
    drug?.id != null && drug?.id !== "" ? pharmacyLocationsMap[drug.id] : null;
  const effectivePharmacyLocation = overridePharmacyLocation ?? drug?.pharmacyLocation;

  const notesMap = useSessionStorageMap(
    "treatmentPrescriptionNotes",
    "treatmentPrescriptionNotesChanged"
  );
  const noteOverride = resolveNoteOverride(notesMap, drug);
  const shouldIncludeNotesOnPrescription = parseBool(
    noteOverride?.includeOnPrescription ?? drug?.includeOnPrescription
  );
  const commentNotesForPrescription = (
    noteOverride?.commentNotes ||
    drug?.commentNotes ||
    drug?.commentsNotes ||
    drug?.comment ||
    ""
  ).trim();

  return (
    <>
      {alertData?.EndServiceAndRefer ? (
        <BillForService />
      ) : (
      <>
      <FormHeader formType={formType} alertData={alertData} pharmacyLocation={effectivePharmacyLocation} />
      {/* patient details */}
      <div className="px-9 py-4 space-y-8">
        <div className="flex flex-row justify-between font-medium">
          {formType === AllergicRhinitisDocTypes.Prescription && (
            <DataField
              value={"Minor Ailment Prescribing Service"}
              className={"!text-[16px]"}
            />
          )}
          <P className="!text-[16px]">
            <span className="text-REELTeal">Service Date</span>{" "}
            {formatToYYYY_MMM_DD(startDateTime)}
          </P>
        </div>
        <div className="flex flex-col gap-2">
          <DataField
            value={(
              (patient.firstName || "") +
              (patient.middleName ? ` ${patient.middleName}` : "") +
              (patient.lastName ? ` ${patient.lastName}` : "")
            ).trim()}
          />
          <DataField
            value={
              patient?.provincialInsurance?.cardNumber
                ? `${formatHealth(patient.provincialInsurance.cardNumber)}-${
                    patient.provincialInsurance.versionCode
                  }`
                : "XXX-XXX-XXX-EF"
            }
          />
          {patient?.contactInfo?.primaryPhone ? (
            <DataField
              value={
                patient?.contactInfo?.primaryPhone
                  ? formatPhoneDocumentation(patient.contactInfo.primaryPhone)
                  : ""
              }
            />
          ) : null}
          <DataField
            value={
              patient.dateOfBirth ? formatToYYYY_MMM_DD(patient.dateOfBirth) : ""
            }
          />
          <GetPatientAddress patient={patient} showLabel={false} />
        </div>
      </div>
      <Divider />
      {/* rx details */}
      <div className="px-9 py-2 flex flex-col gap-4 font-medium [&_dl]:!text-[14px]">
        {effectivePharmacyLocation === "alternate" && (
          <div className="border border-gray-100 bg-gray-10 p-2">
            <DataField
              value={
                <span className="font-semibold text-[14px]">Do not fill Rx as per patient request - Filling at alternate pharmacy location - Bill for Service Only -</span>
              }
            />
          </div>
        )}
        <div className="flex flex-row gap-[0.228333in] items-center">
          <ReactSVG
            src="/icons/Rx-form.svg"
            className="hover:text-gray-700"
            onClick={() => {
              onClose();
            }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <DataField value={drug?.medicationName} />
          <DataField value={`(${drug?.dosage} ${drug?.form} ${drug?.route})`} />
          {drug?.frequency && <DataField
            value={capitalizeFirstLetter(GetDropDownValue(frequencyOptions, drug?.frequency))}
          />}
          {drug?.prn && (
            <div className="grid grid-cols-4">
              <DataField value={"PRN"} />
              <DataField className={"col-span-2"} label={"PRN Reason:"} value={capitalizeFirstLetter(GetDropDownValue(reasonOptions, drug?.prnReason))} />
            </div>
          )}
          <div className="grid grid-cols-4">
            <DataField label={"QTY:"} value={drug?.dispenseQuantity} />
            <DataField label={"Refills:"} value={drug?.refills ?? 0} />
          </div>
          <div className="p-1 w-full"></div>
          <DataField label={"Reason:"} value={capitalizeFirstLetter(GetDropDownValue(reasonOptions, drug?.reason))} />
          {(isValidDate(drug?.startDate) || isValidDate(drug?.endDate)) && (
            <div className="grid grid-cols-2">
              {isValidDate(drug?.startDate) && (
                <DataField
                  label={"Start Date:"}
                  value={formatToYYYY_MMM_DD(drug.startDate)}
                />
              )}
              {isValidDate(drug?.endDate) && (
                <DataField
                  label={"End Date:"}
                  value={formatToYYYY_MMM_DD(drug.endDate)}
                />
              )}
            </div>
          )}
          {drug?.duration && <DataField label={"Duration:"} value={drug?.duration + " " + drug?.unit} />}
          {drug?.comments && (
            <DataField label={"Special Instructions:"} value={capitalizeFirstLetter(drug?.comments)} />
          )}
          {shouldIncludeNotesOnPrescription && commentNotesForPrescription && (
            <DataField
              label={"Comments / Notes:"}
              value={capitalizeFirstLetter(commentNotesForPrescription)}
            />
          )}
          <div className="p-1 w-full"></div>
          <DataField value={"Provincial Drug Coverage:Not Available at this time"} />
        </div>
      </div>
      <Divider />
      {/* pharmacist details */}
      <div className="px-9 py-4 flex flex-col gap-[0.114167in] [&_dl]:!text-[14px]">
        <DataField
          labelTeal={true}
          label={"Prescribing Pharmacist Name"}
          value={pharmacistName}
          className="items-start gap-12"
          noWrapLabel={true}
        />
        <DataField
          labelTeal={true}
          label={"Pharmacist Signature"}
          value={
            pharmacistSignature ? (
              <img
                src={pharmacistSignature}
                alt="Pharmacist Signature"
                className="max-h-[40px] max-w-[200px] object-contain"
              />
            ) : (
              ""
            )
          }
          className="items-start gap-12"
          noWrapLabel={true}
        />
        <DataField
          labelTeal={true}
          label={"Pharmacist License"}
          value={pharmacistLicenseNumber}
          className="items-start gap-12"
          noWrapLabel={true}
        />
      </div>
      {effectivePharmacyLocation !== "alternate" && <Divider />}
      {/* footer details */}
      <div className="px-9 py-4 flex flex-col gap-3">
        {effectivePharmacyLocation !== "alternate" && (
          <div className="border border-gray-100 bg-gray-10 p-2">
            <DataField
              vertical={true}
              labelTeal={true}
              label={"Rx Details"}
              value={"Prescription Issued In Person - PIN 9858181"}
            />
            <DataField
              className="italic"
              label={
                "The Pharmacist billing for the Minor Ailment Service is responsible for ensuring the accuracy of the PIN generated by healthcompassRX."
              }
            />
          </div>
        )}

        <Divider />
        <DataField
          value={
            effectivePharmacyLocation === "alternate"
              ? "Do not fill Rx as per patient request - Filling at alternate pharmacy location - Bill for Service Only"
              : "May fill prescription at pharmacy of choice"
          }
        />

        {!patient.provincialInsurance && (
          <DataField
            className="text-teal-300 font-semibold"
            value={"No Provincial Insurance - Pays for Clinical Services"}
          />
        )}
      </div>
      <FormFooter />
      </>
      )}
    </>
  );
};
